import { MediasHelpers } from './medias/medias.helpers';
import { MediaClazzType, MediaContentTypeType, MediaLocation, MediaResolved } from './medias/medias.models';

import { ProductColor, Product } from '@api/catalog/products/products.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { AkamaiService } from '@shared/services/akamai.service';

export class ProductsHelpers {
    /**
     * Devuelve la primera imagen del primer color de un producto para una localización determinada
     *
     * @param colors Listado de colores de un producto
     * @param location Indica en qué localización (widget) queremos devolver los medias
     * @param [width] Tamaño que va a ocupar el media, por defecto el tamaño máximo posible. Se devolverá el tamaño que más se ajuste
     * @param [clazzType] Indica si queremos devolver solo los medias de un clazz concreto (opcional)
     */
    public static firstImageForFirstColor(
        colors: ProductColor[],
        location: MediaLocation,
        width?: number,
        clazzType?: MediaClazzType,
    ): MediaResolved | undefined {
        let firstImage: MediaResolved | undefined;

        if (colors.length > 0) {
            firstImage = MediasHelpers.firstMediaResolved(colors[0].medias, location, width, clazzType, 'image');
        }

        return firstImage;
    }

    /**
     * Devuelve el listado de imágenes de un color y una localización determinada
     *
     * @param color Color del producto
     * @param location Indica en qué localización (widget) queremos devolver los medias
     * @param [width] Tamaño que va a ocupar el media, por defecto el tamaño máximo posible. Se devolverá el tamaño que más se ajuste
     * @param [clazzType] Indica si queremos devolver solo los medias de un clazz concreto (opcional)
     * @param [impolicy] Indica la calidad de la imagen
     */
    public static imagesForColor(
        window: CommonWindow,
        color: ProductColor,
        location: MediaLocation,
        width?: number,
        clazzType?: MediaClazzType,
        impolicy?: string,

        useAkamai: boolean = true,
    ): MediaResolved[] {
        let contentTypeType: MediaContentTypeType;
        switch (clazzType) {
            case 'video':
            case 'videoDouble':
                contentTypeType = 'video';
                break;
            case 'unknown':
                contentTypeType = 'unknown';
                break;
            default:
                contentTypeType = 'image';
                break;
        }
        const images = MediasHelpers.mediasResolved(color?.medias, location, width, clazzType, contentTypeType);
        let imagesAkamai;
        const isColorCut = location === 'colorcut';
        if (useAkamai && clazzType !== 'video') {
            imagesAkamai = images.map((image) => {
                // eslint-disable-next-line no-param-reassign
                image.path = AkamaiService.useAkamaiProductImage(window, image.path, width, impolicy, isColorCut);
                return image;
            });
        }
        return imagesAkamai || images;
    }

    /**
     * Devuelve todas las imágenes de un producto (incluyendo todos los colores)
     *
     * @param producto sobre el que quieres obtener las imgaenes asociadas
     * @param location Indica en qué localización (widget) queremos devolver los medias
     * @param [width] Tamaño que va a ocupar el media, por defecto el tamaño máximo posible. Se devolverá el tamaño que más se ajuste
     * @param [clazzType] Indica si queremos devolver solo los medias de un clazz concreto (opcional)
     */
    public static allImagesForProduct(
        product: Product,
        location: MediaLocation,
        width?: number,
        clazzType?: MediaClazzType,
    ): MediaResolved[] {
        let imagesForProduct: MediaResolved[] = [];
        for (const color of product.colors) {
            const mediasForColor = MediasHelpers.mediasResolved(color.medias, location, width, clazzType, 'image');
            imagesForProduct = imagesForProduct.concat(mediasForColor);
        }
        return imagesForProduct;
    }
}
